import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { ongoing } from "../data/site";

function ProgressBar({ pct, delay = 0 }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-15%" });
    return (
        <div ref={ref} className="w-full">
            <div className="relative h-[3px] bg-white/10 overflow-hidden">
                <motion.div
                    initial={{ width: 0 }}
                    animate={inView ? { width: `${pct}%` } : {}}
                    transition={{ duration: 1.6, delay, ease: [0.2, 0.7, 0.2, 1] }}
                    className="absolute inset-y-0 left-0 bg-[#C89B3C]"
                />
            </div>
            <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-[0.3em]">
                <span className="text-[#7a7a7a]">Progress</span>
                <span className="text-[#E2C56D]">
                    <motion.span
                        initial={{ opacity: 0 }}
                        animate={inView ? { opacity: 1 } : {}}
                        transition={{ delay: delay + 0.6 }}
                    >
                        {pct}%
                    </motion.span>
                </span>
            </div>
        </div>
    );
}

export default function Ongoing() {
    return (
        <section
            id="ongoing"
            className="relative bg-[#111111] py-24 md:py-40 px-6 md:px-10 border-y border-[rgba(212,175,55,0.2)]"
            data-testid="ongoing-section"
        >
            <div className="mx-auto max-w-[1440px]">
                <div className="flex items-center gap-6 mb-10">
                    <span className="w-16 h-px bg-[#C89B3C]" />
                    <span className="text-[10px] uppercase tracking-[0.4em] text-[#C89B3C]">
                        Chapter 09 — Live from Site
                    </span>
                </div>

                <div className="grid grid-cols-12 gap-6 items-end mb-16">
                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{ duration: 0.9 }}
                        className="col-span-12 lg:col-span-8 font-display font-light text-white text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
                    >
                        Projects
                        <br />
                        Under <em className="text-[#E2C56D]">Construction.</em>
                    </motion.h2>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{ duration: 0.9, delay: 0.15 }}
                        className="col-span-12 lg:col-span-4 text-[#bebebe] text-base leading-relaxed font-light"
                    >
                        Every joint checked, every pour audited, every milestone published.
                        This is the site diary — updated on the ground.
                    </motion.div>
                </div>

                {/* Masonry-esque grid */}
                <div className="grid grid-cols-12 gap-6 md:gap-8">
                    {ongoing.map((o, i) => (
                        <motion.article
                            key={o.name}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-10%" }}
                            transition={{ duration: 0.9, delay: (i % 2) * 0.12 }}
                            className={`group relative col-span-12 md:col-span-6 ${
                                i % 2 === 1 ? "md:translate-y-16" : ""
                            }`}
                            data-testid={`ongoing-${i}`}
                        >
                            <div className="relative aspect-[16/11] overflow-hidden">
                                <img
                                    src={o.img}
                                    alt={o.name}
                                    className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-b from-[#0b0b0b]/60 via-transparent to-[#0b0b0b]" />
                                <div className="absolute inset-0 border border-[rgba(212,175,55,0.25)] group-hover:border-[#C89B3C] transition-colors duration-500" />
                                <div className="absolute top-4 left-4 right-4 flex items-center justify-between text-[10px] uppercase tracking-[0.35em] text-white/95">
                                    <span className="flex items-center gap-3">
                                        <span className="w-2 h-2 rounded-full bg-[#E2C56D] animate-pulse" />
                                        LIVE — {o.stage}
                                    </span>
                                    <span>№ {String(i + 1).padStart(2, "0")}</span>
                                </div>
                            </div>

                            <div className="mt-6 grid grid-cols-12 gap-4 items-start">
                                <div className="col-span-8">
                                    <div className="text-[10px] uppercase tracking-[0.35em] text-[#C89B3C] mb-2">
                                        {o.location}
                                    </div>
                                    <h3 className="font-display text-2xl md:text-3xl text-white leading-tight">
                                        {o.name}
                                    </h3>
                                </div>
                                <div className="col-span-4 flex flex-col items-end">
                                    <div className="font-display text-4xl text-[#E2C56D] leading-none">
                                        {o.pct}%
                                    </div>
                                </div>
                            </div>
                            <div className="mt-5">
                                <ProgressBar pct={o.pct} delay={0.2 + (i % 2) * 0.15} />
                            </div>
                        </motion.article>
                    ))}
                </div>
            </div>
        </section>
    );
}
